// Certified Materials — React Query layer (query keys, list/detail queries,
// filter-option queries, write mutations).

import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  confirmDocumentsReview,
  confirmEvidence,
  deleteDocument,
  deleteOrders,
  fetchAssigneesOptions,
  fetchAvailableOrders,
  fetchBrandsOptions,
  fetchCustomTagsOptions,
  fetchDocumentsPendingReview,
  fetchOrderById,
  fetchOrderDocumentCheck,
  fetchOrderEvidenceItems,
  fetchOrdersList,
  fetchPlatformStandards,
  fetchResponsiblePersonsOptions,
  fetchSeasonsOptions,
  fetchStandardsOptions,
  fetchUsersByCompany,
  fetchVendorsOptions,
  importOrders,
  requestDocumentReupload,
  revokeFieldMatchValidation,
  sendBulkReminders,
  sendBulkRequests,
  updateDocument,
  updateOrder,
  updateOrderStatus,
  uploadDocument,
  uploadOrderDocument,
  validateFieldMatch,
} from '@/api/certified-materials';
import type {
  IApiDocument,
  IApiOrder,
  IApiOrdersResponse,
  IAssignee,
  IConfirmDocumentsReviewBody,
  IEvidenceItem,
  IImportOrdersBody,
  ILinkedOrder,
  ISendRemindersBody,
  ISendRequestEntry,
  IUpdateDocumentBody,
  IUpdateOrderBody,
  IUploadDocBody,
  IUploadOrderDocBody,
} from '@/api/certified-materials/types';
import type { IFilterOption, IOrdersListParams } from '../-types';
import { transformFieldMatchesToDocumentCheck } from '../-utils/orderDetails';

export const certifiedMaterialsKeys = {
  all: ['certified-materials'] as const,
  list: (params: IOrdersListParams) =>
    ['certified-materials', 'orders', 'list', params] as const,
  detail: (orderId: string) =>
    ['certified-materials', 'orders', 'detail', orderId] as const,
  documentCheck: (orderId: string) =>
    ['certified-materials', 'orders', 'document-check', orderId] as const,
  evidenceItems: (orderId: string) =>
    ['certified-materials', 'orders', 'evidence-items', orderId] as const,
  assignees: (companyId: string) =>
    ['certified-materials', 'assignees', companyId] as const,
  filterOptions: (kind: string) =>
    ['certified-materials', 'filter-options', kind] as const,
};

/** Build the query string for the orders-list endpoint. Lives in the hook layer
 * so the api layer stays pure (Rule 3). */
function buildOrdersQs(params: IOrdersListParams): string {
  const sp = new URLSearchParams();
  sp.set('page', String(params.page));
  sp.set('limit', String(params.limit));
  sp.set('sortBy', params.sortBy);
  sp.set('sortOrder', params.sortOrder);
  if (params.quickFilter && params.quickFilter !== 'all')
    sp.set('quickFilter', params.quickFilter);
  sp.set('filter', JSON.stringify(params.filter ?? {}));
  return `?${sp.toString()}`;
}

// Shared between the hook and the route loader so prefetch + render hit the
// same cache entry.
export function ordersQueryOptions(params: IOrdersListParams) {
  return queryOptions({
    queryKey: certifiedMaterialsKeys.list(params),
    queryFn: () => fetchOrdersList(buildOrdersQs(params)),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useOrdersQuery(params: IOrdersListParams) {
  return useQuery(ordersQueryOptions(params));
}

export function useOrderDetailQuery(orderId: string | null) {
  return useQuery({
    queryKey: certifiedMaterialsKeys.detail(orderId ?? ''),
    queryFn: () => fetchOrderById(orderId!),
    enabled: !!orderId,
    staleTime: 15_000,
  });
}

const OPTION_FETCHERS = {
  standards: fetchStandardsOptions,
  vendors: fetchVendorsOptions,
  seasons: fetchSeasonsOptions,
  customTags: fetchCustomTagsOptions,
  assignees: fetchAssigneesOptions,
  brands: fetchBrandsOptions,
  responsiblePersons: fetchResponsiblePersonsOptions,
} as const;

type OptionKind = keyof typeof OPTION_FETCHERS;

export function useFilterOptionsQuery(kind: OptionKind) {
  return useQuery({
    queryKey: certifiedMaterialsKeys.filterOptions(kind),
    queryFn: async (): Promise<IFilterOption[]> => {
      const res = await OPTION_FETCHERS[kind]();
      // API items are { id, name, color? }. Assignees filter by id; everything
      // else filters by name (matches the old frontend).
      const useId = kind === 'assignees';
      return (res.data ?? [])
        .filter(o => !!o?.name)
        .map(o => ({ key: useId ? o.id : o.name, label: o.name }));
    },
    staleTime: 5 * 60_000,
  });
}

const LIST_KEY = ['certified-materials', 'orders', 'list'] as const;

// Optimistically merge `patch` into the cached detail + every cached list page
// for this order. Returns a rollback snapshot.
function applyOptimisticOrderPatch(
  qc: ReturnType<typeof useQueryClient>,
  orderId: string,
  patch: Partial<IApiOrder>,
) {
  const detailKey = certifiedMaterialsKeys.detail(orderId);
  const prevDetail = qc.getQueryData<IApiOrder>(detailKey);
  qc.setQueryData<IApiOrder>(detailKey, old =>
    old ? { ...old, ...patch } : old,
  );
  const prevLists = qc.getQueriesData<IApiOrdersResponse>({
    queryKey: LIST_KEY,
  });
  qc.setQueriesData<IApiOrdersResponse>({ queryKey: LIST_KEY }, old =>
    old
      ? {
          ...old,
          data: old.data.map(o => (o.id === orderId ? { ...o, ...patch } : o)),
        }
      : old,
  );
  return { prevDetail, prevLists, detailKey };
}

type OptimisticCtx = ReturnType<typeof applyOptimisticOrderPatch>;

function rollbackOptimistic(
  qc: ReturnType<typeof useQueryClient>,
  ctx: OptimisticCtx | undefined,
) {
  if (!ctx) return;
  if (ctx.prevDetail) qc.setQueryData(ctx.detailKey, ctx.prevDetail);
  for (const [key, data] of ctx.prevLists) qc.setQueryData(key, data);
}

// Platform standards for the order-line standards editor (value = shortName so
// it matches the strings stored on order lines).
export function useStandardsListQuery() {
  return useQuery({
    queryKey: ['certified-materials', 'platform-standards'] as const,
    queryFn: async (): Promise<IFilterOption[]> => {
      const res = await fetchPlatformStandards();
      const list = res?.data ?? [];
      const seen = new Set<string>();
      const out: IFilterOption[] = [];
      for (const s of list) {
        const label = s.shortName || s.name;
        if (!label || seen.has(label)) continue;
        seen.add(label);
        out.push({ key: label, label });
      }
      return out;
    },
    staleTime: 5 * 60_000,
  });
}

// Free-text custom tags have no server-side tag entity — "creating" one just
// appends it to the cached options so it shows up selected immediately; the
// value persists when the order is saved.
export function useCreateCustomTag() {
  const qc = useQueryClient();
  return async (title: string) => {
    const key = certifiedMaterialsKeys.filterOptions('customTags');
    qc.setQueryData<IFilterOption[]>(key, old => {
      const list = old ?? [];
      return list.some(o => o.key === title)
        ? list
        : [...list, { key: title, label: title }];
    });
    return { value: title, label: title };
  };
}

export function useUpdateOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orderId,
      body,
    }: {
      orderId: string;
      body: IUpdateOrderBody;
    }) => {
      // Apply optimistic patch before the request so the UI updates instantly.
      await qc.cancelQueries({
        queryKey: certifiedMaterialsKeys.detail(orderId),
      });
      await qc.cancelQueries({ queryKey: LIST_KEY });
      const ctx = applyOptimisticOrderPatch(
        qc,
        orderId,
        body as Partial<IApiOrder>,
      );
      try {
        await updateOrder(orderId, body);
        // Mark stale only — optimistic cache is already correct.
        void qc.invalidateQueries({
          queryKey: certifiedMaterialsKeys.all,
          refetchType: 'none',
        });
        void qc.invalidateQueries({
          queryKey: certifiedMaterialsKeys.detail(orderId),
          refetchType: 'none',
        });
      } catch (err) {
        rollbackOptimistic(qc, ctx);
        throw err;
      }
    },
  });
}

export function useUpdateOrderStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orderId,
      status,
    }: {
      orderId: string;
      status: string;
    }) => {
      await qc.cancelQueries({
        queryKey: certifiedMaterialsKeys.detail(orderId),
      });
      await qc.cancelQueries({ queryKey: LIST_KEY });
      const ctx = applyOptimisticOrderPatch(qc, orderId, {
        status: status as IApiOrder['status'],
      });
      try {
        await updateOrderStatus(orderId, status);
        // Mark stale only — optimistic cache is already correct.
        void qc.invalidateQueries({
          queryKey: certifiedMaterialsKeys.all,
          refetchType: 'none',
        });
        void qc.invalidateQueries({
          queryKey: certifiedMaterialsKeys.detail(orderId),
          refetchType: 'none',
        });
      } catch (err) {
        rollbackOptimistic(qc, ctx);
        throw err;
      }
    },
  });
}

export function useImportOrders() {
  return useMutation({
    mutationFn: (body: IImportOrdersBody) => importOrders(body),
  });
}

// Evidence-document upload (POST /documents) — separate from the drawer's
// per-order upload (useUploadOrderDocument).
export function useUploadDocument() {
  return useMutation({
    mutationFn: (body: IUploadDocBody) => uploadDocument(body),
  });
}

const PENDING_REVIEW_QS = `?filter=${encodeURIComponent(JSON.stringify({ reviewStatus: 'pending_review' }))}`;

// Count of documents awaiting review — gates the Review Docs button. Polls so
// the count updates after AI processing finishes for a fresh upload.
export function usePendingReviewCountQuery() {
  return useQuery({
    queryKey: ['certified-materials', 'pending-review-count'] as const,
    queryFn: async (): Promise<number> => {
      const res = await fetchDocumentsPendingReview(PENDING_REVIEW_QS);
      return res.data?.length ?? 0;
    },
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

// Full pending-review document list — fetched only while the review dialog is
// open. The dialog seeds editable local state from this.
export function usePendingReviewDocsQuery(enabled: boolean) {
  return useQuery({
    queryKey: ['certified-materials', 'pending-review-docs'] as const,
    queryFn: async (): Promise<IApiDocument[]> => {
      const res = await fetchDocumentsPendingReview(PENDING_REVIEW_QS);
      return res.data ?? [];
    },
    enabled,
    staleTime: 0,
  });
}

// Orders available to link in the review dialog. Lazy — the /orders?limit=100
// endpoint is heavy (N+1 fan-out), so only fetch while the dialog is open.
export function useAvailableOrdersQuery(enabled: boolean) {
  return useQuery({
    queryKey: ['certified-materials', 'linkable-orders'] as const,
    queryFn: async (): Promise<ILinkedOrder[]> => {
      const res = await fetchAvailableOrders(100);
      return (res.data ?? []).map(o => ({
        id: o.id,
        orderNumber: o.orderNumber,
        articleNumber: o.orderLines?.[0]?.articleNumber ?? '',
      }));
    },
    enabled,
    staleTime: 60_000,
  });
}

export function useConfirmDocumentsReview() {
  return useMutation({
    mutationFn: (body: IConfirmDocumentsReviewBody) =>
      confirmDocumentsReview(body),
  });
}

// Update linked doc extracted data (PATCH /documents/:id) — triggered from
// DocumentDetailsSheet when the user edits and saves extracted fields.
export function useUpdateLinkedDocument() {
  return useMutation({
    mutationFn: ({
      documentId,
      body,
    }: {
      documentId: string;
      body: IUpdateDocumentBody;
    }) => updateDocument(documentId, body),
  });
}

// Plain document delete used by the review dialog (the drawer's
// useDeleteDocument is order-scoped and invalidates drawer queries).
export function useDeleteReviewDocument() {
  return useMutation({
    mutationFn: (documentId: string) => deleteDocument(documentId),
  });
}

export function useDeleteOrders() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      await deleteOrders(ids);
      void qc.invalidateQueries({ queryKey: certifiedMaterialsKeys.all });
    },
  });
}

// Bulk: send document-submission requests to suppliers (brand).
export function useSendBulkRequests() {
  return useMutation({
    mutationFn: (entries: ISendRequestEntry[]) => sendBulkRequests(entries),
  });
}

// Bulk: remind suppliers about pending requests (brand).
export function useSendBulkReminders() {
  return useMutation({
    mutationFn: (body: ISendRemindersBody) => sendBulkReminders(body),
  });
}

// Bulk: submit selected orders for review (supplier). Status updates run
// sequentially — the cert-materials service has no batch status endpoint.
export function useSubmitOrdersForReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderIds: string[]) => {
      for (const orderId of orderIds) {
        await updateOrderStatus(orderId, 'in_review');
      }
      void qc.invalidateQueries({ queryKey: certifiedMaterialsKeys.all });
      return orderIds;
    },
  });
}

// ── Order-details drawer ─────────────────────────────────────────────────────

export function useOrderDocumentCheckQuery(
  orderId: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: certifiedMaterialsKeys.documentCheck(orderId ?? ''),
    queryFn: async () => {
      const matches = await fetchOrderDocumentCheck(orderId!);
      return transformFieldMatchesToDocumentCheck(matches);
    },
    enabled: !!orderId && enabled,
    staleTime: 15_000,
  });
}

export function useOrderEvidenceItemsQuery(orderId: string | null) {
  return useQuery({
    queryKey: certifiedMaterialsKeys.evidenceItems(orderId ?? ''),
    queryFn: () => fetchOrderEvidenceItems(orderId!),
    enabled: !!orderId,
    staleTime: 15_000,
  });
}

export function useAssigneesQuery(companyId: string | null) {
  return useQuery({
    queryKey: certifiedMaterialsKeys.assignees(companyId ?? ''),
    queryFn: async (): Promise<IAssignee[]> => {
      const res = await fetchUsersByCompany(companyId!);
      const list = Array.isArray(res) ? res : (res.data ?? []);
      return list.map(u => ({
        id: u.id,
        name: u.name || u.email,
        email: u.email,
      }));
    },
    enabled: !!companyId,
    staleTime: 5 * 60_000,
  });
}

function useDetailInvalidator() {
  const qc = useQueryClient();
  return (orderId: string) => {
    void qc.invalidateQueries({
      queryKey: certifiedMaterialsKeys.all,
      refetchType: 'none',
    });
    void qc.invalidateQueries({
      queryKey: certifiedMaterialsKeys.detail(orderId),
      refetchType: 'none',
    });
  };
}

export function useConfirmEvidence(orderId: string) {
  const invalidate = useDetailInvalidator();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      evidenceId,
      confirmed,
    }: {
      evidenceId: string;
      confirmed: boolean;
    }) => {
      // Optimistic update
      const key = certifiedMaterialsKeys.evidenceItems(orderId);
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<IEvidenceItem[]>(key);
      const itemId = evidenceId.split(':').slice(0, -1).join(':');
      qc.setQueryData<IEvidenceItem[]>(key, old =>
        old?.map(item => {
          if (item.id !== itemId) return item;
          const requiredEvidence = item.requiredEvidence.map(e =>
            e.id === evidenceId ? { ...e, isCompleted: confirmed } : e,
          );
          return {
            ...item,
            requiredEvidence,
            completedCount: requiredEvidence.filter(e => e.isCompleted).length,
          };
        }),
      );
      try {
        await confirmEvidence(orderId, evidenceId, confirmed);
        void qc.invalidateQueries({
          queryKey: certifiedMaterialsKeys.evidenceItems(orderId),
          refetchType: 'none',
        });
        invalidate(orderId);
      } catch (err) {
        // Rollback optimistic update
        if (prev) qc.setQueryData(key, prev);
        throw err;
      }
    },
  });
}

export function useUploadOrderDocument(orderId: string) {
  const invalidate = useDetailInvalidator();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: IUploadOrderDocBody) => {
      await uploadOrderDocument(orderId, body);
      void qc.invalidateQueries({
        queryKey: certifiedMaterialsKeys.evidenceItems(orderId),
      });
      invalidate(orderId);
    },
  });
}

export function useDeleteDocument(orderId: string) {
  const invalidate = useDetailInvalidator();
  return useMutation({
    mutationFn: async (documentId: string) => {
      await deleteDocument(documentId);
      invalidate(orderId);
    },
  });
}

export function useRequestReupload() {
  return useMutation({
    mutationFn: ({
      documentId,
      message,
    }: {
      documentId: string;
      message: string;
    }) => requestDocumentReupload(documentId, message),
  });
}

export function useFieldMatchValidation(orderId: string) {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({
      queryKey: certifiedMaterialsKeys.documentCheck(orderId),
    });
    void qc.invalidateQueries({ queryKey: certifiedMaterialsKeys.all });
  };
  const validate = useMutation({
    mutationFn: async ({
      matchId,
      fieldName,
    }: {
      matchId: string;
      fieldName: string;
    }) => {
      await validateFieldMatch(matchId, fieldName);
      invalidate();
    },
  });
  const revoke = useMutation({
    mutationFn: async ({
      matchId,
      fieldName,
    }: {
      matchId: string;
      fieldName: string;
    }) => {
      await revokeFieldMatchValidation(matchId, fieldName);
      invalidate();
    },
  });
  return { validate, revoke };
}
